import tippy from 'tippy.js';

class MainProduct {
  constructor(element) {
    this.view = element;
    const variantsJsonElement = document.querySelector('[data-product-variants-json]');
    if (!variantsJsonElement) {
      console.error('Product variants JSON data not found.');
      return;
    }
    this.variants = JSON.parse(variantsJsonElement.textContent);
    this.priceWrapper = this.view.querySelector('[data-price-wrapper]');
    this.productForm = this.view.querySelector('#main-product-form');
    if (!this.productForm) {
      console.error('Product form not found.');
      return;
    }
    this.productId = variantsJsonElement.getAttribute('data-product-id');
    this.productTitle = variantsJsonElement.getAttribute('data-product-title');
    this.addToCartButton = this.productForm.querySelector('[data-add-to-cart-button]');
    this.addToCartButtonText = this.productForm.querySelector('[data-add-to-cart-text]');
    this.hiddenVariantInput = this.productForm.querySelector('input[name="id"]');
    this.currentVariant = this.variants.find((v) => +v.id === +this.hiddenVariantInput.value);

    if (
      !window.location.search.includes('?variant=') &&
      variantsJsonElement.hasAttribute('data-has-size-variants') &&
      this.variants.length > 1
    ) {
      this.currentVariant = null;
    }

    this.updateBreadcrumbs();
    this.initSizePicker();
    this.initDenominationPicker();
    this.initProductInfoTabs();
    this.bindEvents();
    this.updateAddToCartButton();
    this.initPinToPinterestBoard();
  }

  handleCreateStockAlertClick() {
    const variantId = this.currentVariant?.id;
    const productId = +this.productId;
    const productTitle = this.currentVariant?.name;

    if (variantId && productId && productTitle) {
      window.CenteredPopovers.wrapper.show({
        name: 'coming-soon-alert',
        payload: {
          variantId: variantId,
          productId: productId,
          title: productTitle,
        },
      });
    }
  }

  handleCreateBackInStock(variantId, productTitle) {
    if (variantId && productTitle) {
      window.CenteredPopovers.wrapper.show({
        name: 'back-in-stock',
        payload: {
          variantId: variantId,
          title: productTitle,
        },
      });
    }
  }

  initProductInfoTabs() {
    const tabsElement = this.view.querySelector('.el-product-info-tabs');
    if (tabsElement) {
      void new ProductInfoTabs(tabsElement);
    }
  }

  bindEvents() {
    this.view.addEventListener('variant:select', this.onVariantSelect.bind(this));
    this.productForm.addEventListener('submit', this.onAddToCart.bind(this));
    this.initWishlistButton();
  }

  initWishlistButton() {
    this.wishlistButton = this.view.querySelector('.js-add-to-wishlist-btn');
    if (!this.wishlistButton) {
      return;
    }

    this.wishlistButton.addEventListener('click', this.onAddToWishlist.bind(this));

    // Listen for wishlist updates to refresh button state
    document.addEventListener('wishlist:updated', () => {
      this.updateWishlistButtonState();
    });

    // Initialize tooltip and button state after Tippy is ready
    // Use setTimeout to ensure Tippy has been initialized by Bootstrap
    setTimeout(() => {
      this.updateWishlistTooltip();
    }, 100);

    // Wait for SDK to be ready, then update button state
    this._waitForSDKAndUpdateButtonState();
  }

  _waitForSDKAndUpdateButtonState() {
    if (window.WishListHero_SDK) {
      this._loadAndUpdateButtonState();
    } else {
      document.addEventListener('wishlist-hero-wishlist-sdk-ready', () => {
        this._loadAndUpdateButtonState();
      });
    }
  }

  async _loadAndUpdateButtonState() {
    if (elabs?.wishlistDrawer) {
      await elabs.wishlistDrawer.ensureItemsLoaded();
      this.updateWishlistButtonState();
    }
  }

  updateWishlistTooltip() {
    if (!this.wishlistButton) {
      return;
    }

    const hasSizeVariants = this.wishlistButton.dataset.hasSizeVariants === 'true';

    // If product has size variants and no variant selected, show tooltip
    if (hasSizeVariants && !this.currentVariant) {
      // Enable tooltip
      if (this.wishlistButton._tippy) {
        this.wishlistButton._tippy.enable();
      }
    } else {
      // Disable tooltip when variant is selected
      if (this.wishlistButton._tippy) {
        this.wishlistButton._tippy.disable();
      }
    }
  }

  updateWishlistButtonState() {
    if (!this.wishlistButton || !elabs?.wishlistDrawer) {
      return;
    }

    const variantId = this.currentVariant?.id || this.variants[0]?.id;
    const isInWishlist = elabs.wishlistDrawer.isVariantInWishlist(variantId);

    this.wishlistButton.classList.toggle('is-in-wishlist', isInWishlist);
    this.wishlistButton.setAttribute('aria-pressed', isInWishlist ? 'true' : 'false');
  }

  async onAddToWishlist(event) {
    event.preventDefault();

    if (!elabs?.wishlistDrawer) {
      console.error('Wishlist drawer not available');
      return;
    }

    const btn = this.wishlistButton;
    const hasSizeVariants = btn.dataset.hasSizeVariants === 'true';
    const variantId = this.currentVariant?.id || this.variants[0]?.id;

    // Check if variant is already in wishlist - if so, remove it
    if (variantId && elabs.wishlistDrawer.isVariantInWishlist(variantId)) {
      await elabs.wishlistDrawer.removeFromWishlist(variantId);
      this.updateWishlistButtonState();
      return;
    }

    const productInfo = {
      productId: btn.dataset.productId,
      title: btn.dataset.productTitle,
      url: btn.dataset.productUrl,
      image: btn.dataset.productImage,
      price: btn.dataset.productPrice,
    };

    // If product has size variants
    if (hasSizeVariants) {
      if (this.currentVariant) {
        // User selected a variant - add specific variant to wishlist
        productInfo.variantId = this.currentVariant.id;
        productInfo.url = `${btn.dataset.productUrl}?variant=${this.currentVariant.id}`;
        productInfo.price = (this.currentVariant.price / 100).toFixed(2);
        await elabs.wishlistDrawer.addToWishlist(productInfo, true, true);
      } else {
        // No variant selected - add first available variant without variant marker (will show "Select size" in wishlist)
        const firstAvailableVariant = this.variants.find((v) => v.available) || this.variants[0];
        productInfo.variantId = firstAvailableVariant?.id;
        productInfo.url = btn.dataset.productUrl;
        await elabs.wishlistDrawer.addToWishlist(productInfo, false, true);
      }
    } else {
      // Product without size variants - add directly
      const variant = this.currentVariant || this.variants[0];
      productInfo.variantId = variant?.id;
      productInfo.url = `${btn.dataset.productUrl}?variant=${variant?.id}`;
      productInfo.price = variant ? (variant.price / 100).toFixed(2) : btn.dataset.productPrice;
      await elabs.wishlistDrawer.addToWishlist(productInfo, true, true);
    }

    this.updateWishlistButtonState();
  }

  onVariantSelect(event) {
    const { variantId } = event.detail;
    this.currentVariant = this.variants.find((v) => +v.id === +variantId);
    this.updateAddToCartButton();
    this.updateWishlistTooltip();
    this.updateWishlistButtonState();
    if (!this.currentVariant) {
      return;
    }
    this.updatePrice();
    this.updateUrl();
    this.updateFormVariant();
  }

  updateAddToCartButton() {
    if (!this.addToCartButton || !this.addToCartButtonText) {
      return;
    }

    if (!this.currentVariant) {
      this.productForm.classList.add('main-product__buy-button-form--unselected-variant');
      return;
    } else {
      this.productForm.classList.remove('main-product__buy-button-form--unselected-variant');
    }

    if (
      this.addToCartButtonText.classList.contains('main-product__buy-button-text--coming-soon') ||
      this.addToCartButtonText.classList.contains('main-product__buy-button-text--sold-out')
    ) {
      this.addToCartButton.disabled = false;
      return;
    }

    if (this.currentVariant.available) {
      this.addToCartButton.disabled = false;
      this.addToCartButtonText.textContent = 'Add to Cart';
      this.productForm.classList.remove('main-product__buy-button-form--variant-sold-out');
    } else {
      // Enable button so user can click to create stock alert
      this.addToCartButton.disabled = false;
      this.addToCartButtonText.textContent = 'CREATE STOCK ALERT';
      this.productForm.classList.add('main-product__buy-button-form--variant-sold-out');
    }
  }

  updatePrice() {
    if (!this.priceWrapper) {
      return;
    }
    const { productUrl } = this.productForm.dataset;
    fetch(`${productUrl}?variant=${this.currentVariant.id}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newPrice = html.querySelector('[data-price-wrapper]');
        if (newPrice) {
          this.priceWrapper.innerHTML = newPrice.innerHTML;
        }
      });
  }

  updateUrl() {
    const { productUrl } = this.productForm.dataset;
    window.history.replaceState({}, '', `${productUrl}?variant=${this.currentVariant.id}`);
  }

  updateFormVariant() {
    this.hiddenVariantInput.value = this.currentVariant.id;
  }

  onAddToCart(event) {
    event.preventDefault();

    if (this.productForm.classList.contains('main-product__buy-button-form--unselected-variant')) {
      return;
    }

    if (this.productForm.classList.contains('main-product__buy-button-form--coming-soon')) {
      this.handleCreateStockAlertClick();
      return;
    }

    if (this.addToCartButtonText.classList.contains('main-product__buy-button-text--sold-out')) {
      if (this.currentVariant) {
        this.handleCreateBackInStock(this.currentVariant.id, this.currentVariant.name);
      }
      return;
    }

    // Handle sold out variant on partially available product
    if (this.productForm.classList.contains('main-product__buy-button-form--variant-sold-out')) {
      if (this.currentVariant) {
        this.handleCreateBackInStock(this.currentVariant.id, this.currentVariant.name);
      }
      return;
    }

    this.addToCartButton.classList.add('is-loading');
    this.addToCartButton.disabled = true;

    const formData = new FormData(this.productForm);

    formData.append('sections', 'el-cart-drawer-inner,cart-icon-bubble');
    formData.append('sections_url', window.location.pathname);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => {
        if (response.status === 422) {
          return response.json().then((err) => {
            if (err?.description?.email) {
              throw new Error(err?.description?.email || 'Request could not be processed.');
            }
            throw new Error(err.description || 'Request could not be processed.');
          });
        }
        return response.json();
      })
      .then((response) => {
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: {
              source: 'main-product-component',
              cart: response,
            },
          }),
        );
      })
      .catch((error) => {
        console.error('Error:', error);
        alert(error.message || 'There was an error adding the item to your cart.');
      })
      .finally(() => {
        this.addToCartButton.classList.remove('is-loading');
        if (this.currentVariant) {
          this.addToCartButton.disabled = !this.currentVariant.available;
        }
      });
  }

  initSizePicker() {
    const sizePickerElement = this.view.querySelector('.main-product__size-picker');
    if (sizePickerElement) {
      void new SizePicker(sizePickerElement, this.currentVariant?.id);
    }
  }

  initDenominationPicker() {
    const denominationPickerElement = this.view.querySelector('.main-product__denomination-picker');
    if (denominationPickerElement) {
      void new DenominationPicker(denominationPickerElement, this.currentVariant?.id);
    }
  }

  updateBreadcrumbs() {
    const referrer = document.referrer;
    const visibilityWrapper = document.querySelector('.main-product__breadcrumbs-wrapper');
    const contentTarget = document.querySelector('.breadcrumb-collection-wrapper');

    if (!visibilityWrapper || !contentTarget) {
      return;
    }

    const makeVisible = () => visibilityWrapper.classList.add('is-visible');

    try {
      if (!referrer) {
        makeVisible();
        return;
      }

      const referrerUrl = new URL(referrer);
      const currentHost = window.location.hostname;

      if (referrerUrl.hostname === currentHost && referrerUrl.pathname.includes('/collections/')) {
        const forbiddenPaths = ['/collections/all', '/collections/boost-all'];
        const isForbiddenCollection = forbiddenPaths.some((path) => referrerUrl.pathname.startsWith(path));

        if (!isForbiddenCollection) {
          try {
            const storedData = localStorage.getItem('lastVisitedCollection');
            if (storedData) {
              const collectionData = JSON.parse(storedData);

              if (collectionData && referrerUrl.pathname.includes(collectionData.handle)) {
                contentTarget.innerHTML = `<span class="main-product__breadcrumb-divider">/</span><a href="${collectionData.url}" class="main-product__breadcrumb">${collectionData.title}</a>`;
              }
            }
          } catch (e) {
            console.error('Could not read or parse collection data from localStorage:', e);
          }
        }
      }

      makeVisible();
    } catch (error) {
      console.error('Error processing referrer URL for breadcrumbs:', error);
      makeVisible();
    }
  }

  initPinToPinterestBoard() {
    const pinToPinterestBtn = this.view.querySelector('.main-product__pin-to-pinterest-btn');
    if (!pinToPinterestBtn) {
      return;
    }

    const productUrl = pinToPinterestBtn.dataset.pinterestProductUrl;
    const productTitle = pinToPinterestBtn.dataset.pinterestProductTitle;

    if (!productUrl || !productTitle) {
      pinToPinterestBtn.style.display = 'none';
      return;
    }

    const mediaContainers = [...this.view.querySelectorAll('.main-product__media')];

    const images = mediaContainers
      .map((container) => [...container.querySelectorAll('img')])
      .flat()
      .filter(
        (img) =>
          img.getAttribute('src') || img.dataset.src || img.currentSrc || img.getAttribute('data_max_resolution'),
      );

    if (!images.length) {
      pinToPinterestBtn.style.display = 'none';
      return;
    }

    const tooltipRoot = document.createElement('div');
    tooltipRoot.className = 'main-product__pin-to-pinterest--tooltip';

    const titleEl = document.createElement('h5');
    titleEl.className = 'main-product__pin-to-pinterest--tooltip-title';
    titleEl.textContent = 'Choose an image to add to your Pinterest Board:';
    tooltipRoot.appendChild(titleEl);

    const grid = document.createElement('div');
    grid.className = 'main-product__pin-to-pinterest--tooltip-img-grid';
    tooltipRoot.appendChild(grid);

    const encodedDescription = encodeURIComponent(productTitle.trim());

    const buildSizedSrc = (rawSrc, targetWidth) => {
      if (!rawSrc) {
        return null;
      }

      try {
        const url = new URL(rawSrc, window.location.origin);
        url.searchParams.set('width', targetWidth);

        let href = url.href;

        if (rawSrc.startsWith('//')) {
          href = href.replace(/^https?:/, '');
        }

        return href;
      } catch (e) {
        if (rawSrc.includes('width=')) {
          return rawSrc.replace(/width=\d+/g, `width=${targetWidth}`);
        }

        const sep = rawSrc.includes('?') ? '&' : '?';
        return `${rawSrc + sep}width=${targetWidth}`;
      }
    };

    const usedThumbSrc = new Set();

    images.forEach((img) => {
      const rawSrc =
        img.getAttribute('data_max_resolution') || img.getAttribute('src') || img.dataset.src || img.currentSrc;

      if (!rawSrc) {
        return;
      }

      const thumbSrc = buildSizedSrc(rawSrc, 300);
      const mediaSrc = rawSrc;

      if (!thumbSrc || !mediaSrc) {
        return;
      }

      if (usedThumbSrc.has(thumbSrc)) {
        return;
      }
      usedThumbSrc.add(thumbSrc);

      const a = document.createElement('a');
      a.href = `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(
        productUrl,
      )}&media=${encodeURIComponent(mediaSrc)}&description=${encodedDescription}`;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';

      const tooltipImg = document.createElement('img');
      tooltipImg.src = thumbSrc;
      tooltipImg.alt = img.getAttribute('alt') || productTitle || '';
      tooltipImg.width = 300;
      tooltipImg.height = 300;

      a.appendChild(tooltipImg);
      grid.appendChild(a);
    });

    if (!grid.children.length) {
      pinToPinterestBtn.style.display = 'none';
      return;
    }

    const isTouchDevice = 'ontouchstart' in document.documentElement;
    tippy(pinToPinterestBtn, {
      trigger: isTouchDevice ? 'click' : 'mouseenter focus',
      allowHTML: true,
      interactive: true,
      interactiveBorder: 30,
      placement: 'top-end',
      theme: 'pin-to-pinterest-sophiawebster',
      appendTo: () => document.body,
      content: tooltipRoot,
    });
  }
}

class ProductInfoTabs {
  constructor(element) {
    this.container = element;
    this.tabs = this.container.querySelectorAll('[role="tab"]');
    this.panels = this.container.querySelectorAll('[role="tabpanel"]');
    this.bindEvents();
  }

  bindEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleTabClick.bind(this));
    });
  }

  handleTabClick(event) {
    const clickedTab = event.currentTarget;

    if (clickedTab.getAttribute('aria-selected') === 'true') {
      return;
    }

    this.tabs.forEach((tab) => {
      tab.classList.remove('is-active');
      tab.setAttribute('aria-selected', 'false');
    });
    this.panels.forEach((panel) => {
      panel.classList.remove('is-active');
      panel.setAttribute('hidden', true);
    });

    clickedTab.classList.add('is-active');
    clickedTab.setAttribute('aria-selected', 'true');

    const controlledPanelId = clickedTab.getAttribute('aria-controls');
    const controlledPanel = this.container.querySelector(`#${controlledPanelId}`);
    if (controlledPanel) {
      controlledPanel.classList.add('is-active');
      controlledPanel.removeAttribute('hidden');
    }
  }
}

class SizePicker {
  constructor(element, initialVariantId = null) {
    this.container = element;
    this.systemsContainer = this.container.querySelector('.main-product__size-picker-systems');
    this.systemButtons = this.container.querySelectorAll('.main-product__size-picker-system');
    this.dropdown = this.container.querySelector('.main-product__size-picker-dropdown');
    this.dropdownButton = this.container.querySelector('.main-product__size-picker-dropdown-btn');
    this.dropdownButtonText = this.container.querySelector('[data-size-picker-text]');
    this.dropdownList = this.container.querySelector('.main-product__size-picker-dropdown-list');
    this.dropdownListInner = this.container.querySelector('.main-product__size-picker-dropdown-list-inner');
    this.initialSizeItems = this.container.querySelectorAll('.main-product__size-picker-dropdown-item');
    this.infoTabsContainer = document.querySelector('.main-product__details .el-product-info-tabs');

    this.variants = [];
    this.currentSystem = 'EU';
    this.selectedVariantId = initialVariantId;
    this.sizeFallbackMap = {
      '16/17': { uk: '0-1', us: '1-2' },
      '18/19': { uk: '2-3', us: '3-4' },
      21: { uk: '4.5', us: '5' },
      22: { uk: '5', us: '6' },
      23: { uk: '6', us: '7' },
      24: { uk: '7', us: '8' },
      25: { uk: '8', us: '9' },
      26: { uk: '8.5', us: '9.5' },
      27: { uk: '9', us: '10' },
      28: { uk: '10', us: '11' },
      29: { uk: '10.5', us: '11.5' },
      30: { uk: '11', us: '12' },
      31: { uk: '12', us: '13' },
      32: { uk: '12.5', us: '13.5' },
      33: { uk: '13', us: '1' },
      34: { uk: '1', us: '2' },
      35: { uk: '2', us: '4.5' },
      35.5: { uk: '2.5', us: '5' },
      36: { uk: '3', us: '5.5' },
      36.5: { uk: '3.5', us: '6' },
      37: { uk: '4', us: '6.5' },
      37.5: { uk: '4.5', us: '7' },
      38: { uk: '5', us: '7.5' },
      38.5: { uk: '5.5', us: '8' },
      39: { uk: '6', us: '8.5' },
      39.5: { uk: '6.5', us: '9' },
      40: { uk: '7', us: '9.5' },
      40.5: { uk: '7.5', us: '10' },
      41: { uk: '8', us: '10.5' },
      41.5: { uk: '8.5', us: '11' },
      42: { uk: '9', us: '11.5' },
      43: { uk: '10', us: '12.5' },
      44: { uk: '11', us: '13.5' },
      45: { uk: '12', us: '14.5' },
      46: { uk: '13', us: '15.5' },
    };

    this.init();
  }

  init() {
    this.parseVariantData();
    this.initSizingSystem();
    this.orderSizingSystems();
    this.render();
    this.bindEvents();

    if (this.selectedVariantId) {
      const selectedVariant = this.variants.find((v) => +v.id === +this.selectedVariantId);
      if (selectedVariant) {
        this.dropdownButtonText.textContent = this.getSizeForSystem(selectedVariant, this.currentSystem);
        this.updateInfoTabs(selectedVariant);
      }
    }

    this.dropdownButtonText.classList.remove('main-product__size-picker-dropdown-btn-text--hidden');

    if (this.systemsContainer) {
      this.systemsContainer.classList.remove('main-product__size-picker-systems--hidden');
    }
  }

  orderSizingSystems() {
    if (!this.systemsContainer) {
      return;
    }

    const activeButton = this.container.querySelector(`[data-size-system="${this.currentSystem}"]`);
    if (activeButton) {
      this.systemsContainer.prepend(activeButton);
    }
  }

  parseVariantData() {
    this.initialSizeItems.forEach((item) => {
      this.variants.push({
        id: item.dataset.variantId,
        title: item.dataset.productTitle,
        sku: item.dataset.variantSku,
        available: !item.hasAttribute('disabled'),
        size_eu: item.dataset.sizeEu,
        size_uk: item.dataset.sizeUk,
        size_us: item.dataset.sizeUs,
      });
    });
  }

  initSizingSystem() {
    const preferredSystem = localStorage.getItem('preferred_sizing');
    if (['EU', 'UK', 'US'].includes(preferredSystem)) {
      this.currentSystem = preferredSystem;
      return;
    }

    if (this.systemsContainer) {
      const currentCountry = this.systemsContainer.dataset.currentCountry;
      /* eslint-disable indent */
      switch (currentCountry) {
        case 'GB':
          this.currentSystem = 'UK';
          break;
        case 'US':
          this.currentSystem = 'US';
          break;
        default:
          this.currentSystem = 'EU';
          break;
      }
    }
  }

  bindEvents() {
    if (this.systemsContainer) {
      this.systemButtons.forEach((button) => button.addEventListener('click', this.handleSystemChange.bind(this)));
    }
    this.dropdownButton.addEventListener('click', this.toggleDropdown.bind(this));
    this.dropdownList.addEventListener('click', (event) => {
      const target = event.target.closest('.main-product__size-picker-dropdown-item');
      if (target && !target.hasAttribute('disabled')) {
        this.handleSizeSelect(target);
      }
    });

    document.addEventListener('click', (event) => {
      if (!this.container.contains(event.target) || event.target.closest('.tippy-content')) {
        this.closeDropdown();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeDropdown();
      }
    });

    this.dropdownListInner.addEventListener('click', (event) => {
      const targetElement = event.target.closest('.stock-alert-btn, .main-product__size-picker-dropdown-item-wrapper');

      if (!targetElement) {
        return;
      }

      const { variantId, productTitle } = targetElement.dataset;

      if (variantId && productTitle) {
        // Select the sold out variant (so it can be added to wishlist)
        this.handleSoldOutSizeSelect(variantId);

        // Show stock alert popover
        window.CenteredPopovers.wrapper.show({
          name: 'back-in-stock',
          payload: {
            variantId: variantId,
            title: productTitle,
          },
        });
      }
    });
  }

  /**
   * Handle selection of a sold out size variant
   * This allows the variant to be selected (for wishlist) while showing stock alert
   */
  handleSoldOutSizeSelect(variantId) {
    this.selectedVariantId = variantId;
    const selectedVariant = this.variants.find((v) => +v.id === +variantId);

    // Update UI to show selected state
    const previouslySelected = this.dropdownListInner.querySelector('.is-selected');
    if (previouslySelected) {
      previouslySelected.classList.remove('is-selected');
      previouslySelected.setAttribute('aria-selected', 'false');
    }

    // Find and mark the sold out button as selected
    const soldOutButton = this.dropdownListInner.querySelector(
      `.main-product__size-picker-dropdown-item[data-variant-id="${variantId}"]`,
    );
    if (soldOutButton) {
      soldOutButton.classList.add('is-selected');
      soldOutButton.setAttribute('aria-selected', 'true');
    }

    // Update dropdown button text
    if (selectedVariant) {
      this.dropdownButtonText.textContent = this.getSizeForSystem(selectedVariant, this.currentSystem);
    }

    // Dispatch variant:select event so MainProduct updates currentVariant
    this.container.dispatchEvent(
      new CustomEvent('variant:select', {
        bubbles: true,
        detail: { variantId: variantId },
      }),
    );

    this.closeDropdown();
    this.updateInfoTabs(selectedVariant);
  }

  render() {
    this.systemButtons.forEach((btn) => {
      const isSelected = btn.dataset.sizeSystem === this.currentSystem;
      btn.classList.toggle('is-active', isSelected);
      btn.setAttribute('aria-selected', isSelected);
    });

    if (this.selectedVariantId) {
      const selectedVariant = this.variants.find((v) => +v.id === +this.selectedVariantId);
      if (selectedVariant) {
        this.dropdownButtonText.textContent = this.getSizeForSystem(selectedVariant, this.currentSystem);
      }
    } else {
      this.dropdownButtonText.textContent = 'Select size';
    }

    this.dropdownListInner.innerHTML = '';

    this.variants.forEach((variant) => {
      const sizeValue = this.getSizeForSystem(variant, this.currentSystem);
      const button = document.createElement('button');
      const isSelected = +variant.id === +this.selectedVariantId;

      button.className = 'main-product__size-picker-dropdown-item';
      if (isSelected) {
        button.classList.add('is-selected');
      }

      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', isSelected);
      button.dataset.variantId = variant.id;
      button.textContent = sizeValue;

      if (!variant.available) {
        const variantId = variant.id;
        const productTitle = `${variant.title} - ${sizeValue}`;

        button.setAttribute('disabled', true);
        button.setAttribute('aria-label', `Size ${sizeValue} - Sold out`);

        const wrapper = document.createElement('div');
        wrapper.className = 'main-product__size-picker-dropdown-item-wrapper';
        wrapper.setAttribute('data-variant-id', variantId);
        wrapper.setAttribute('data-product-title', productTitle);

        const buttonHTML = `
          <button 
            class="stock-alert-btn" 
            data-variant-id="${variantId}" 
            data-product-title="${productTitle}"
          >
            Create stock alert
          </button>
        `;

        wrapper.setAttribute('data-tippy-content', buttonHTML);
        wrapper.setAttribute('data-tippy-allowhtml', true);
        wrapper.setAttribute('data-tippy-interactive', true);
        wrapper.setAttribute('data-tippy-append-to', 'parent');

        wrapper.appendChild(button);

        this.dropdownListInner.appendChild(wrapper);
      } else {
        this.dropdownListInner.appendChild(button);
      }
    });

    const selector = `#${this.container.id} .main-product__size-picker-dropdown-item-wrapper[data-tippy-content]`;
    elabs.Bootstrap.initialiseTooltips(selector);
  }

  getSizeForSystem(variant, system) {
    const euSize = variant.size_eu;
    switch (system) {
      case 'UK':
        return variant.size_uk || this.sizeFallbackMap[euSize]?.uk || euSize;
      case 'US':
        return variant.size_us || this.sizeFallbackMap[euSize]?.us || euSize;
      default:
        return euSize;
    }
  }

  handleSystemChange(event) {
    const newSystem = event.currentTarget.dataset.sizeSystem;
    if (newSystem === this.currentSystem) {
      return;
    }
    this.currentSystem = newSystem;
    localStorage.setItem('preferred_sizing', newSystem);
    this.render();
  }

  handleSizeSelect(selectedButton) {
    this.selectedVariantId = selectedButton.dataset.variantId;
    const selectedVariant = this.variants.find((v) => +v.id === +this.selectedVariantId);

    const previouslySelected = this.dropdownListInner.querySelector('.is-selected');
    if (previouslySelected) {
      previouslySelected.classList.remove('is-selected');
      previouslySelected.setAttribute('aria-selected', 'false');
    }
    selectedButton.classList.add('is-selected');
    selectedButton.setAttribute('aria-selected', 'true');

    this.dropdownButtonText.textContent = selectedButton.textContent;

    this.container.dispatchEvent(
      new CustomEvent('variant:select', {
        bubbles: true,
        detail: { variantId: this.selectedVariantId },
      }),
    );

    this.closeDropdown();
    this.updateInfoTabs(selectedVariant);
  }

  updateInfoTabs(selectedVariant) {
    if (!this.infoTabsContainer || !selectedVariant) {
      return;
    }

    const productCodeEl = this.infoTabsContainer.querySelector('[data-product-code]');
    if (selectedVariant.sku) {
      productCodeEl.textContent = selectedVariant.sku;
    } else {
      productCodeEl.textContent = productCodeEl.dataset.defaultCode;
    }
  }

  toggleDropdown() {
    const isActive = this.dropdown.classList.contains('is-active');
    if (isActive) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    this.dropdown.classList.add('is-active');
    this.dropdownButton.setAttribute('aria-expanded', 'true');
  }

  closeDropdown() {
    this.dropdown.classList.remove('is-active');
    this.dropdownButton.setAttribute('aria-expanded', 'false');
  }
}

class DenominationPicker {
  constructor(element, initialVariantId = null) {
    this.container = element;
    this.dropdown = this.container.querySelector('.main-product__denomination-picker-dropdown');
    this.dropdownButton = this.container.querySelector('.main-product__denomination-picker-dropdown-btn');
    this.dropdownButtonText = this.container.querySelector('[data-denomination-picker-text]');
    this.dropdownList = this.container.querySelector('.main-product__denomination-picker-dropdown-list');
    this.dropdownListInner = this.container.querySelector('.main-product__denomination-picker-dropdown-list-inner');
    this.initialDenominationItems = this.container.querySelectorAll('.main-product__denomination-picker-dropdown-item');

    this.variants = [];
    this.selectedVariantId = initialVariantId;

    this.init();
  }

  init() {
    this.parseVariantData();
    this.render();
    this.bindEvents();

    if (this.selectedVariantId) {
      const selectedVariant = this.variants.find((v) => +v.id === +this.selectedVariantId);
      if (selectedVariant) {
        this.dropdownButtonText.textContent = selectedVariant.denomination;
      }
    }

    this.dropdownButtonText.classList.remove('main-product__denomination-picker-dropdown-btn-text--hidden');
  }

  parseVariantData() {
    this.initialDenominationItems.forEach((item) => {
      this.variants.push({
        id: item.dataset.variantId,
        title: item.dataset.productTitle,
        denomination: item.dataset.denomination,
        available: !item.hasAttribute('disabled'),
      });
    });
  }

  bindEvents() {
    this.dropdownButton.addEventListener('click', this.toggleDropdown.bind(this));
    this.dropdownList.addEventListener('click', (event) => {
      const target = event.target.closest('.main-product__denomination-picker-dropdown-item');
      if (target && !target.hasAttribute('disabled')) {
        this.handleDenominationSelect(target);
      }
    });

    document.addEventListener('click', (event) => {
      if (!this.container.contains(event.target) || event.target.closest('.tippy-content')) {
        this.closeDropdown();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeDropdown();
      }
    });
  }

  render() {
    this.dropdownListInner.innerHTML = '';

    this.variants.forEach((variant) => {
      const button = document.createElement('button');
      const isSelected = +variant.id === +this.selectedVariantId;

      button.className = 'main-product__denomination-picker-dropdown-item';
      if (isSelected) {
        button.classList.add('is-selected');
      }

      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', isSelected);
      button.dataset.variantId = variant.id;
      button.textContent = variant.denomination;

      if (!variant.available) {
        button.setAttribute('disabled', true);
        button.setAttribute('aria-label', `Denomination ${variant.denomination} - Sold out`);
      }

      this.dropdownListInner.appendChild(button);
    });
  }

  handleDenominationSelect(selectedButton) {
    this.selectedVariantId = selectedButton.dataset.variantId;
    const selectedVariant = this.variants.find((v) => +v.id === +this.selectedVariantId);

    const previouslySelected = this.dropdownListInner.querySelector('.is-selected');
    if (previouslySelected) {
      previouslySelected.classList.remove('is-selected');
      previouslySelected.setAttribute('aria-selected', 'false');
    }
    selectedButton.classList.add('is-selected');
    selectedButton.setAttribute('aria-selected', 'true');

    this.dropdownButtonText.textContent = selectedButton.textContent;

    this.container.dispatchEvent(
      new CustomEvent('variant:select', {
        bubbles: true,
        detail: { variantId: this.selectedVariantId },
      }),
    );

    this.closeDropdown();
  }

  toggleDropdown() {
    const isActive = this.dropdown.classList.contains('is-active');
    if (isActive) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    this.dropdown.classList.add('is-active');
    this.dropdownButton.setAttribute('aria-expanded', 'true');
  }

  closeDropdown() {
    this.dropdown.classList.remove('is-active');
    this.dropdownButton.setAttribute('aria-expanded', 'false');
  }
}

class PdpVimeoPlayer {
  constructor(container) {
    this.container = container;
    this.iframe = container.querySelector('iframe');
    this.playBtn = container.querySelector('.js-pdp-vimeo-play-btn');
    this.player = null;
    this.hasChecked = false;

    if (!this.iframe || !this.playBtn) {
      return;
    }

    this.initObserver();
  }

  initObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasChecked) {
            this.hasChecked = true;
            this.observer.unobserve(this.container);
            this.checkAutoplay();
          }
        });
      },
      { rootMargin: '0px 0px 100px 0px' },
    );

    this.observer.observe(this.container);
  }

  async checkAutoplay() {
    await this.loadVimeoAPI();
    this.player = new Vimeo.Player(this.iframe);

    try {
      await this.player.ready();
    } catch {
      this.showPlayButton();
      return;
    }

    setTimeout(() => {
      this.player
        .getPaused()
        .then((paused) => {
          if (paused) {
            this.showPlayButton();
          }
        })
        .catch(() => {
          this.showPlayButton();
        });
    }, 2500);
  }

  showPlayButton() {
    this.playBtn.style.display = 'flex';

    this.playBtn.addEventListener('click', () => {
      this.player
        .play()
        .then(() => {
          this.playBtn.style.display = 'none';
        })
        .catch(() => {});
    });
  }

  loadVimeoAPI() {
    return new Promise((resolve) => {
      if (window.Vimeo && window.Vimeo.Player) {
        resolve();
        return;
      }

      elabs.DependencyService.fetchDependency('https://player.vimeo.com/api/player.js', resolve);
    });
  }

  static initAll() {
    const containers = document.querySelectorAll('.js-pdp-vimeo-container');
    if (!containers.length) {
      return;
    }

    containers.forEach((container) => new PdpVimeoPlayer(container));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.main-product').forEach((element) => {
    void new MainProduct(element);
  });

  PdpVimeoPlayer.initAll();
});
